import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Instagram, Linkedin, Mail, ArrowUpRight, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialLinks = {
  instagram: 'https://www.instagram.com/buzzcraftmaven/',
  linkedin: 'https://www.linkedin.com/company/buzzcraftmaven/',
  email: 'https://mail.google.com/mail/?view=cm&fs=1&to=buzzcraftmaven@gmail.com'
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' })
      });
      const data = await res.json();
      if (res.ok) {
        if (data.status === 'already_subscribed') {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
        setEmail('');
      } else {
        toast.error(data.detail || 'Subscription failed');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };
  return (
    <footer className="relative bg-gradient-to-b from-[#FFFBF5] to-[#FFE8D6] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] sun-orb sun-orb-soft opacity-50" />

      <div className="container mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
        {/* Top CTA */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#2D2D2D] leading-tight mb-4">
              Ready to design your
              <br />
              <span className="text-gradient-sunrise">brand experience?</span>
            </h2>
            <p className="text-[#5A5A5A] mb-8">
              Let's craft something meaningful together.
            </p>
            <Link to="/contact" data-testid="footer-cta">
              <motion.button
                className="btn-sunrise text-lg px-10 py-4"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="flex items-center gap-2">
                  Start a Project
                  <ArrowUpRight size={20} />
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>

        <div className="horizon-line mb-12" />

        {/* Footer Grid */}
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Brand */}
          <div className="col-span-12 md:col-span-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_growthcollective/artifacts/ejzrxoai_Footer.png" 
              alt="Buzz Craft Maven - Creative Consultancy" 
              className="h-12 w-auto object-contain mb-4"
              loading="lazy"
            />
            <p className="text-[#5A5A5A] text-sm leading-relaxed max-w-xs mb-6">
              A modern creative consultancy designing brands, spaces, and experiences for businesses that demand more.
            </p>
            <div className="flex items-center gap-3">
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram"
                className="w-10 h-10 rounded-full bg-white border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all shadow-sm">
                <Instagram size={18} />
              </a>
              <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn"
                className="w-10 h-10 rounded-full bg-white border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all shadow-sm">
                <Linkedin size={18} />
              </a>
              <a href={socialLinks.email} target="_blank" rel="noopener noreferrer" data-testid="footer-email" aria-label="Email"
                className="w-10 h-10 rounded-full bg-white border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all shadow-sm">
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Digital Services */}
          <div className="col-span-6 md:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-6">Digital</h4>
            <nav className="flex flex-col gap-3" aria-label="Digital services">
              {['Brand Strategy', 'Performance Marketing', 'Social Media', 'Content Production', 'Website Design'].map(s => (
                <Link key={s} to="/digital" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">{s}</Link>
              ))}
            </nav>
          </div>

          {/* Interior Services */}
          <div className="col-span-6 md:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-6">Interiors</h4>
            <nav className="flex flex-col gap-3" aria-label="Interior services">
              {['Residential', 'Commercial', 'Workspace Design', 'Retail Spaces', 'Hospitality'].map(s => (
                <Link key={s} to="/interiors" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">{s}</Link>
              ))}
            </nav>
          </div>

          {/* Company */}
          <div className="col-span-6 md:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-6">Company</h4>
            <nav className="flex flex-col gap-3" aria-label="Company links">
              <Link to="/about" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">About</Link>
              <Link to="/blog" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">Insights</Link>
              <Link to="/contact" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">Contact</Link>
              <Link to="/case-studies" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">Case Studies</Link>
              <a href="/api/sitemap.xml" className="text-[#5A5A5A] hover:text-[#FF6B35] transition-colors text-sm link-warm w-fit">Sitemap</a>
            </nav>
          </div>

          {/* Contact + Newsletter */}
          <div className="col-span-6 md:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-6">Stay Updated</h4>
            <p className="text-[#5A5A5A] text-sm mb-4">Get our latest insights on digital marketing and design — straight to your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex gap-2 mb-6" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-3 py-2.5 bg-white border border-[#FF6B35]/20 rounded-lg text-sm text-[#2D2D2D] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#FF6B35] transition-colors"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="px-3 py-2.5 bg-[#FF6B35] text-white rounded-lg hover:bg-[#e55a2a] transition-colors disabled:opacity-50"
                data-testid="newsletter-submit-btn"
              >
                <Send size={16} />
              </button>
            </form>
            <p className="text-[#5A5A5A] text-sm mb-1">buzzcraftmaven@gmail.com</p>
            <p className="text-[#8A8A8A] text-sm mb-3">Chennai &middot; Hyderabad &middot; Dubai</p>
            <Link to="/audit" className="text-[#FF6B35] text-sm font-medium hover:underline">Request a Free Audit &rarr;</Link>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-[#FF6B35]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#8A8A8A] text-sm">
            &copy; {new Date().getFullYear()} Buzz Craft Maven. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-[#8A8A8A] text-sm">
            <span>Privacy Policy</span>
            <span>Terms & Conditions</span>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Available for projects</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
